import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Order } from '../../interfaces/orders/order';
import { Subject, takeUntil } from 'rxjs';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { Province } from '../../interfaces/province';
import { BusinessSearchResultRepositoryService } from '../../services/business-search-results/business-search-result-repository.service';
import { BusinessSearchResult } from '../../interfaces/business-search-results/business-search-result';
import { ListComponent } from '../list-component';
import { animate, query, state, style, transition, trigger, sequence } from '@angular/animations';
import { FormGroup } from '@angular/forms';
import { OrderRepositoryService } from '../../services/order/order-repository-service.service';
import { Router } from '@angular/router';
import { SelectOption } from '../../interfaces/select-option';
import { eOrderStatusType, eOrderType, ePartyType, eResultState, eResultStateID } from '../../enums';
import { BusinessReportForBusinessSearchResult } from '../../interfaces/business-reports-for-business-search-result/business-search-result';
import { HttpParams } from '@angular/common/http';
import { ConfigurationState } from '../../services/configuration-state/configuration-state';
import { Utilities } from '../../utilities';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'lib-business-search-results',
  templateUrl: './business-search-results.component.html',
  styleUrls: ['./business-search-results.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    trigger('loadingToLoaded', [
      transition('* => Complete', [
        sequence([
          // Animate the spinner out first
          query('.spinner-animation', [
            style({ height: '*', opacity: 1 }),
            animate('300ms ease-in', style({ height: '0', opacity: 0 }))
          ], { optional: true }),
          // After spinner is fully gone, animate results in
          query('.results-animation', [
            style({ height: '0', opacity: 0, overflow: 'hidden' }),
            animate('300ms ease-out', style({ height: '*', opacity: 1 }))
          ], { optional: true })
        ])
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BusinessSearchResultsComponent extends ListComponent<BusinessSearchResult> implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @Input() showOrderReportButton: boolean = true;
  @Input() isAdmin: boolean = false;
  @Input() showHiddenFilter: boolean = false;
  protected order!: Order;
  protected businessSearchResults!: BusinessSearchResult[];
  protected suggestedResult?: BusinessSearchResult;
  protected _expandedResult: BusinessSearchResult | null = null;
  protected displayedColumns: string[] = ['name', 'number', 'jurisdiction', 'status', 'orderReport'];
  protected columnsToDisplayWithView = [...this.displayedColumns, 'indicator'];
  protected baseOrderDetails!: FormGroup;
  protected isSaving: boolean = false;
  protected shouldShowOrderPpsaSearch = false;

  protected resultJurisdictions: SelectOption[] = [];
  protected resultStatuses: SelectOption[] = [];
  protected resultTypes: SelectOption[] = [];
  protected resultDescriptions: SelectOption[] = [];
  protected resultJurisdictionStatuses: SelectOption[] = [];

  provinces: Province[] = [
    { value: 'AB', viewValue: 'AB' },
    { value: 'BC', viewValue: 'BC' },
    { value: 'MB', viewValue: 'MB' },
    { value: 'NB', viewValue: 'NB' },
    { value: 'NL', viewValue: 'NL' },
    { value: 'NS', viewValue: 'NS' },
    { value: 'ON', viewValue: 'ON' },
    { value: 'PE', viewValue: 'PE' },
    { value: 'QC', viewValue: 'QC' },
    { value: 'SK', viewValue: 'SK' },
    { value: 'NT', viewValue: 'NT' },
    { value: 'NU', viewValue: 'NU' },
    { value: 'YT', viewValue: 'YT' },
    { value: '_F', viewValue: 'FED' }
  ]

  private unsubscribe$ = new Subject<void>();

  constructor(
    private orderManager: OrderManagerService,
    private ordersRepo: OrderRepositoryService,
    private router: Router,
    private configurationState: ConfigurationState,
    repo: BusinessSearchResultRepositoryService) {
    super(repo)
  }

  ngOnInit(): void {
    this.order = this.orderManager.currentOrder.value;
    this.orderManager.currentOrder
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(order => {
        this.order = order;
        this.repo.populateBaseLink([this.order.id]);
        let defaultParams = new HttpParams().set("SortColumn", "Score").set("SortDirection", "Asc").set("ShowResult", true);

        this.repo.getFirstPageSubscribable(defaultParams).subscribe(results => {
          if (this.order.businessSearchCriteria?.attestorSelectedNumber) {
            const number = Utilities.removeNonAlphaNumericChars(this.order.businessSearchCriteria.attestorSelectedNumber);
            this.suggestedResult = results.find(r => Utilities.removeNonAlphaNumericChars(r.number) === number);
          }
        });
      });

    this.configurationState.canDraftOrderType(eOrderType.debtorSearch).then(canDraft => this.shouldShowOrderPpsaSearch = canDraft);

    if (this.isAdmin) {
      this.displayedColumns = this.displayedColumns.concat(["source", "score"]);
      this.columnsToDisplayWithView = this.columnsToDisplayWithView.concat(["source", "score"]);
    } else {
      this.displayedColumns = this.displayedColumns.concat(["actions"]);
      this.columnsToDisplayWithView = this.columnsToDisplayWithView.concat(["actions"]);
    }

    if (this.showOrderReportButton == false) {
      this.displayedColumns = this.displayedColumns.filter(column => column !== 'orderReport');
      this.columnsToDisplayWithView = [...this.displayedColumns, 'indicator'];
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getJurisdiction(result: BusinessSearchResult) {
    var jurisdiction = result.jurisdiction;
    if (result.jurisdiction == "_F") {
      jurisdiction = "FED";
    }

    if (this.isExtraProvincial(result)) {
      let homeJurisdiction = result.homeJurisdiction ? result.homeJurisdiction : 'unknown';
      jurisdiction = `${result.jurisdiction} (EP - ${homeJurisdiction != "_F" ? homeJurisdiction : "FED"})`;
    }

    return jurisdiction;
  }

  isExtraProvincial(result: BusinessSearchResult) {
    return (result.isExtraJurisdictional);
  }

  getExtraProvincialExplanation(result: BusinessSearchResult) {
    if (result.homeJurisdiction) {
      return this.transloco.translate('businessSearchResults.extraProvincialWithHome', { homeJurisdiction: result.homeJurisdiction != "_F" ? result.homeJurisdiction : "FED" })
    } else {
      return this.transloco.translate('businessSearchResults.extraProvincialUnknownHome')
    }
  }

  orderReport(result: BusinessSearchResult) {
    this.isSaving = true;
    // The way Wolters Kluwer displays their results is very funky.
    // a Kentucky search can show a Montana business whose home jurisdiction is in Florida
    // we still have to show the search was done at the Kentucky Sec of State.
    const jurisdiction = this.order.country == "US" ? this.order.jurisdiction : result.jurisdiction;

    const order = {
      referenceNumber: this.order.referenceNumber,
      country: this.order.country,
      jurisdiction: jurisdiction,
      orderTypeID: eOrderType.businessReport,
      uboTreeNodeID: this.order.businessSearchCriteria?.uboTreeNodeID,
      orderGroupID: this.order.orderGroupID,
      businessSearchResultID: result.id,
      businessReportCriteria: {
        number: result.number,
        businessName: result.name
      }
    }

    this.ordersRepo.createNewOrder(order)
      .subscribe(result => {
        const originatingOrderID = this.order.id;
        this.isSaving = false;
        const order = (result as any).resource;
        this.order = order;
        this.orderManager.updateOrder(order);
        if (this.order.id) {
          this.router.navigate(['orders', 'business', order.id], {
            queryParams: {
              originatingOrderID: originatingOrderID,
              destination: "originating"
            }
          });
        }
      })
  }

  orderPpsaSearch(result: BusinessSearchResult) {
    const order = {
      referenceNumber: this.order.referenceNumber,
      orderTypeID: eOrderType.debtorSearch,
      country: this.order.country,
      jurisdiction: result.jurisdiction,
      parties: [
        {
          busName: result.name,
          partyTypeID: ePartyType.businessDebtor
        }
      ],
      searchParameters: {
        exactOnlyResults: false
      }
    }

    this.isSaving = true;
    this.ordersRepo.createNewOrder(order).subscribe(result => {
      this.isSaving = false;

      if (result.resource.id) {
        this.router.navigate(['orders', 'ppsa-search', result.resource.id]);
      }
    });
  }

  navigateToOrder(result: BusinessSearchResult) {
    const timeOrderedReports = result.businessReports?.sort((a, b) => {
      return new Date(b.added).getTime() - new Date(a.added).getTime();
    });

    const mostRecentReport = timeOrderedReports?.[0];

    if (mostRecentReport) {
      this.router.navigate(['/orders', mostRecentReport.businessReportOrderID]);
    }
  }

  resultsOverflow() {
    return this.order.searchResult?.resultState.id == eResultState.resultsOverflow ||
      this.order.searchResult?.resultState.id == eResultStateID.resultsOverflow;
  }

  noResults() {
    return this.order.searchResult?.resultState.id == eResultState.noResult ||
      this.order.searchResult?.resultState.id == eResultStateID.noResult;
  }

  shouldShowOrderReportButton(result: BusinessSearchResult): boolean {
    const reportList = (result.businessReports ?? []);
    return reportList.length === 0;
  }

  shouldShowViewReportButton(result: BusinessSearchResult): boolean {
    return (result.businessReports ?? []).length > 0
  }

  shouldShowPendingReportButton(result: BusinessSearchResult): boolean {
    return this.filterPendingReports(result.businessReports).length > 0
  }

  filterPendingReports(reports: BusinessReportForBusinessSearchResult[] | null | undefined) {
    // if the report isnt in draft and isnt in a complete or cancelled status then it should be seen as pending
    return reports?.filter(report => report.businessReportOrderOrderStatusTypeID !== eOrderStatusType.draft
      && report.businessReportOrderOrderStatusTypeID !== eOrderStatusType.complete
      && report.businessReportOrderOrderStatusTypeID !== eOrderStatusType.cancelled) ?? [];
  }

  isReportAvailable(result: BusinessSearchResult): boolean {
    return result.isReportAvailable;
  }
}
